import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { artistsApi } from "../../api";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import type { Contact } from "../../types";
import { formatLinkedArtists } from "../../utils/contactForm";
import { formatContactRole, sortContactsByRole } from "../../utils/contactProfile";
import "./KeyContactsPanel.css";

type Props = {
  contacts: Contact[];
  contactsPath: string;
  title?: string;
  emptyMessage?: string;
  limit?: number;
  loading?: boolean;
};

const KeyContactsPanel: React.FC<Props> = ({
  contacts,
  contactsPath,
  title = "Key contacts",
  emptyMessage = "No contacts yet. Add agents and managers in Contacts.",
  limit = 6,
  loading = false,
}) => {
  const { token } = useAuth();
  const artistsQuery = useQuery({
    queryKey: token ? queryKeys.artists.list(APP_WORKSPACE) : queryKeys.artists.all(APP_WORKSPACE),
    queryFn: () => artistsApi.list(token!),
    enabled: Boolean(token),
  });
  const rows = useMemo(() => sortContactsByRole(contacts).slice(0, limit), [contacts, limit]);

  return (
    <div className="app-shell-dash-panel key-contacts-panel">
      <div className="app-shell-dash-panel-head">
        <h2 className="app-shell-dash-panel-title">{title}</h2>
        <Link to={contactsPath} className="app-shell-inline-link key-contacts-panel__manage-link">
          Manage contacts →
        </Link>
      </div>
      <div className="app-shell-dash-panel-body">
        {loading ? (
          <p className="key-contacts-panel__empty">Loading contacts…</p>
        ) : rows.length === 0 ? (
          <p className="key-contacts-panel__empty">
            {emptyMessage}{" "}
            <Link to={contactsPath} className="app-shell-inline-link">
              Open Contacts
            </Link>
          </p>
        ) : (
          <ul className="key-contacts-panel__list">
            {rows.map((contact) => (
              <li key={contact.id} className="key-contacts-panel__item">
                <div className="key-contacts-panel__main">
                  <span className="key-contacts-panel__name">{contact.name}</span>
                  <span className="app-shell-pill key-contacts-panel__role-pill">{formatContactRole(contact.role)}</span>
                </div>
                {contact.agency ? <div className="key-contacts-panel__agency">{contact.agency}</div> : null}
                {contact.artists.length ? (
                  <div className="key-contacts-panel__artists" title="Linked artists">
                    {formatLinkedArtists(contact, artistsQuery.data ?? [])}
                  </div>
                ) : null}
                <div className="key-contacts-panel__actions">
                  {contact.phone ? (
                    <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="eventos-nav-btn key-contacts-panel__action">
                      Call
                    </a>
                  ) : null}
                  {contact.email ? (
                    <a href={`mailto:${contact.email}`} className="eventos-nav-btn key-contacts-panel__action">
                      Email
                    </a>
                  ) : null}
                  {!contact.phone && !contact.email ? (
                    <span className="key-contacts-panel__no-channel">No phone or email</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default KeyContactsPanel;
